import { getUserFromLS } from './getUserFromLS';

export class TokenExpiration {
  private static readonly EXPIRATION_BUFFER_MINUTES = 5; // Check 5 minutes before expiration
  private static readonly WARNING_BUFFER_MINUTES = 10; // Show warning 10 minutes before expiration
  private static checkInterval: NodeJS.Timeout | null = null;
  private static warningShown = false;
  private static logoutFunction: (() => void) | null = null;

  /**
   * Set the logout function to use when token expires
   */
  static setLogoutFunction(logoutFn: () => void): void {
    this.logoutFunction = logoutFn;
  }

  /**
   * Check if the current token is expired or will expire soon
   */
  static isTokenExpired(): boolean {
    const user = getUserFromLS();
    
    if (!user || !user.token_expires_at) {
      return true;
    }

    const expirationDate = new Date(user.token_expires_at);
    const bufferTime = new Date(Date.now() + (this.EXPIRATION_BUFFER_MINUTES * 60 * 1000));
    
    return expirationDate <= bufferTime;
  }

  /**
   * Check if we should show a warning about token expiration
   */
  private static shouldShowWarning(): boolean {
    const user = getUserFromLS();
    
    if (!user || !user.token_expires_at) {
      return false;
    }

    const expirationDate = new Date(user.token_expires_at);
    const warningTime = new Date(Date.now() + (this.WARNING_BUFFER_MINUTES * 60 * 1000));
    
    return expirationDate <= warningTime;
  }

  /**
   * Start monitoring token expiration
   */
  static startMonitoring(): void {
    // Clear any existing interval
    this.stopMonitoring();
    
    // Reset warning flag
    this.warningShown = false;
    
    // Check every minute
    this.checkInterval = setInterval(() => {
      if (this.isTokenExpired()) {
        this.handleTokenExpiration();
      } else if (this.shouldShowWarning() && !this.warningShown) {
        this.showWarningNotification();
        this.warningShown = true;
      }
    }, 60 * 1000); // Check every minute
  }

  /**
   * Stop monitoring token expiration
   */
  static stopMonitoring(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    // Reset warning flag
    this.warningShown = false;
  }

  /**
   * Handle token expiration by showing notification and logging out
   */
  static handleTokenExpiration(): void {
    // Stop monitoring first
    this.stopMonitoring();
    
    // Show user-friendly notification
    this.showExpirationNotification();
    

    if (this.logoutFunction) {
      setTimeout(() => {
        this.logoutFunction!();
      }, 2000); // 2 second delay
    }
  }

  /**
   * Show a warning notification about upcoming session expiration
   */
  private static showWarningNotification(): void {
    alert('⚠️ Your session will expire in 5 minutes');
  }

  /**
   * Show a user-friendly notification about session expiration
   */
  private static showExpirationNotification(): void {
    alert('🔒 Your session has expired. You will be redirected to the home page.');
  }
} 